"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { uploadFileWithPresignedUrl } from "./uploadFile";
const formatBytes = (value) => {
    if (value < 1024) {
        return `${value} B`;
    }
    const units = ["KB", "MB", "GB"];
    let size = value;
    let unitIndex = -1;
    do {
        size /= 1024;
        unitIndex += 1;
    } while (size >= 1024 && unitIndex < units.length - 1);
    return `${size.toFixed(1)} ${units[unitIndex]}`;
};
export const UploadDropzone = ({ onRequestUpload, onUploadComplete, onUploadError, accept, maxSize, disabled, className, label = "Drag and drop files here", description = "or click to browse", fetchImplementation, uploadingLabel = "Uploading {fileName}… {progress}%", successLabel = "{fileName} uploaded successfully.", uploadAnotherLabel = "Upload another file", }) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [activeFile, setActiveFile] = useState(null);
    const reset = () => {
        setStatus("idle");
        setError(null);
        setProgress(0);
        setActiveFile(null);
    };
    const handleUpload = useCallback(async (file) => {
        setActiveFile(file.name);
        setStatus("uploading");
        setError(null);
        setProgress(0);
        try {
            if (maxSize && file.size > maxSize) {
                throw new Error(`File exceeds maximum size of ${formatBytes(maxSize)}`);
            }
            const instructions = await onRequestUpload(file);
            const { response, etag } = await uploadFileWithPresignedUrl(file, instructions, {
                fetchImplementation,
                onProgress: (uploaded, total) => {
                    if (total > 0) {
                        setProgress(Math.round((uploaded / total) * 100));
                    }
                },
            });
            setStatus("success");
            setProgress(100);
            onUploadComplete?.({ file, instructions, etag, response });
        }
        catch (err) {
            const errorInstance = err instanceof Error ? err : new Error("Upload failed");
            setError(errorInstance.message);
            setStatus("error");
            onUploadError?.(file, errorInstance);
        }
    }, [fetchImplementation, maxSize, onRequestUpload, onUploadComplete, onUploadError]);
    const processFiles = useCallback(async (files) => {
        const [file] = Array.from(files);
        if (!file) {
            return;
        }
        await handleUpload(file);
    }, [handleUpload]);
    const handleInputChange = useCallback(async (event) => {
        if (event.target.files) {
            await processFiles(event.target.files);
            event.target.value = "";
        }
    }, [processFiles]);
    const handleDrop = useCallback(async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (disabled) {
            return;
        }
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            await processFiles(files);
        }
    }, [disabled, processFiles]);
    const handleDragOver = (event) => {
        event.preventDefault();
        if (!disabled) {
            setIsDragging(true);
        }
    };
    const handleDragLeave = () => {
        setIsDragging(false);
    };
    const openFileDialog = () => {
        if (!disabled) {
            inputRef.current?.click();
        }
    };
    const statusMessage = (() => {
        if (status === "uploading" && activeFile) {
            return uploadingLabel
                .replace("{fileName}", activeFile)
                .replace("{progress}", String(progress));
        }
        if (status === "success" && activeFile) {
            return successLabel.replace("{fileName}", activeFile);
        }
        if (status === "error" && error) {
            return error;
        }
        return null;
    })();
    return (_jsxs("div", { className: clsx("grid gap-2", className), children: [_jsxs("div", { role: "button", tabIndex: disabled ? -1 : 0, onClick: openFileDialog, onKeyDown: (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openFileDialog();
                    }
                }, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, className: clsx("group flex h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/20 bg-white/5 px-4 text-center text-sm transition", isDragging && !disabled ? "border-gruv-electric-lime/70 bg-gruv-electric-lime/10 shadow-[0_0_0_1px_rgba(196,255,77,0.3)]" : "", disabled
                    ? "cursor-not-allowed opacity-60"
                    : "hover:border-gruv-electric-lime/60 hover:bg-gruv-electric-lime/5 hover:shadow-[0_0_0_1px_rgba(196,255,77,0.2)]"), children: [_jsx("input", { ref: inputRef, type: "file", className: "hidden", accept: accept?.join(","), onChange: handleInputChange, disabled: disabled }), _jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", fill: "none", className: clsx("h-6 w-6 opacity-60 transition", !disabled && "group-hover:opacity-100 group-hover:text-gruv-light-lime"), children: _jsx("path", { d: "M12 16V4m0 0-4 4m4-4 4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("div", { className: clsx("font-medium transition", !disabled && "group-hover:text-gruv-light-lime"), children: label }), _jsx("div", { className: "text-xs opacity-80", children: description }), maxSize ? _jsxs("div", { className: "text-xs opacity-50", children: ["Max size: ", formatBytes(maxSize)] }) : null] }), statusMessage ? (_jsx("div", { className: clsx("text-xs", {
                    "text-white/80": status === "uploading" || status === "success",
                    "text-red-400": status === "error",
                }), children: statusMessage })) : null, status === "success" ? (_jsx("button", { type: "button", className: "text-xs text-white/60 underline", onClick: reset, children: uploadAnotherLabel })) : null] }));
};
